import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AuthModal({ initialMode = "login", onClose }) {
  const navigate = useNavigate();
  const [mode, setMode]         = useState(initialMode);
  const [flipping, setFlipping] = useState(false);

  const [loginForm, setLoginForm]       = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", password: "" });
  const [otp, setOtp]                   = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [success, setSuccess]   = useState("");

  /* ── Flip animation ── */
  const flipTo = (target) => {
    if (flipping || target === mode) return;
    setFlipping(true);
    setError("");
    setSuccess("");
    setTimeout(() => {
      setMode(target);
      setFlipping(false);
    }, 300);
  };

  /* ── Login ── */
  const handleLogin = async () => {
    if (!loginForm.email || !loginForm.password) {
      setError("Please fill in all fields.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API_BASE_URL}/auth/login`, loginForm);
      localStorage.setItem("token", res.data.token);
      onClose();
      window.location.reload();
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || "";
      if (msg.toLowerCase().includes("verif")) {
        const email = loginForm.email;
        setPendingEmail(email);
        // Auto-send OTP so existing (pre-verification) users get a code immediately
        try {
          await axios.post(`${API_BASE_URL}/auth/resend-otp`, { email });
        } catch (_) {
          // ignore — user can still manually resend on the verify screen
        }
        flipTo("verify");
      } else {
        setError(msg || "Invalid email or password.");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Register ── */
  const handleRegister = async () => {
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      setError("Please fill in all fields.");
      return;
    }

    if (registerForm.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API_BASE_URL}/auth/register`, registerForm);
      setPendingEmail(registerForm.email);
      flipTo("verify");
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.message || "";
      if (msg.toLowerCase().includes("already exists")) {
        setError("__account_exists__");
      } else {
        setError(msg || "Registration failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Verify OTP ── */
  const handleVerify = async () => {
    if (!otp || otp.length < 4) {
      setError("Please enter the OTP sent to your email.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API_BASE_URL}/auth/verify-email`, {
        email: pendingEmail,
        otp,
      });
      localStorage.setItem("token", res.data.token);
      onClose();
      window.location.reload();
    } catch (e) {
      setError(e.response?.data?.message || "Invalid or expired OTP.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Resend OTP ── */
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setSuccess("");
    setError("");

    try {
      await axios.post(`${API_BASE_URL}/auth/resend-otp`, { email: pendingEmail });
      setSuccess("A new OTP has been sent to your email.");
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to resend OTP.");
    }
  };

  /* ── Enter key ── */
  const handleKey = (e) => {
    if (e.key !== "Enter") return;
    if (mode === "login")    handleLogin();
    if (mode === "register") handleRegister();
    if (mode === "verify")   handleVerify();
  };

  return (
    <>
      <style>{`
        .auth-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(3px);
          z-index: 200;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: auth-fade-in 0.2s ease;
        }

        @keyframes auth-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .auth-modal {
          width: 100%;
          max-width: 420px;
          margin: 16px;
          perspective: 1000px;
        }

        .auth-card {
          background: var(--bg-card);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          overflow: hidden;
          transition: transform 0.3s ease, opacity 0.3s ease;
          transform-origin: center center;
        }

        .auth-card.flipping {
          transform: rotateY(90deg);
          opacity: 0;
        }

        .auth-top-bar {
          display: flex;
          border-bottom: 1px solid var(--border-subtle);
        }

        .auth-tab {
          flex: 1;
          padding: 16px;
          background: transparent;
          border: none;
          font-family: var(--font-sans);
          font-size: 11px;
          font-weight: 400;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          cursor: pointer;
          transition: color var(--transition), background var(--transition);
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
        }

        .auth-tab.active {
          color: var(--accent);
          border-bottom-color: var(--accent);
        }

        .auth-tab:not(.active) {
          color: var(--text-muted);
        }

        .auth-tab:not(.active):hover {
          color: var(--text-secondary);
          background: var(--bg-hover);
        }

        .auth-tab:disabled {
          cursor: default;
          opacity: 0.4;
        }

        .auth-body {
          padding: 32px;
        }

        .auth-logo {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 24px;
        }

        .auth-logo-text {
          font-family: var(--font-serif);
          font-size: 20px;
          color: var(--text-logo);
          letter-spacing: 0.01em;
        }

        .auth-title {
          font-family: var(--font-serif);
          font-size: 26px;
          font-weight: 400;
          color: var(--text-logo);
          margin: 0 0 6px;
        }

        .auth-subtitle {
          font-size: 13px;
          color: var(--text-muted);
          margin: 0 0 28px;
          line-height: 1.5;
        }

        .auth-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: transparent;
          border: 1px solid var(--border-default);
          color: var(--text-muted);
          width: 28px;
          height: 28px;
          border-radius: var(--radius-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 16px;
          line-height: 1;
          transition: color var(--transition), border-color var(--transition);
        }

        .auth-close:hover {
          color: var(--text-primary);
          border-color: var(--text-secondary);
        }

        .auth-footer {
          text-align: center;
          margin-top: 20px;
          font-size: 12px;
          color: var(--text-muted);
        }

        .auth-footer-link {
          background: none;
          border: none;
          font-family: var(--font-sans);
          font-size: 12px;
          color: var(--accent);
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          text-underline-offset: 2px;
          transition: opacity var(--transition);
        }

        .auth-footer-link:hover { opacity: 0.8; }
        .auth-footer-link:disabled { opacity: 0.4; cursor: default; }

        .auth-error {
          font-size: 12px;
          color: var(--danger-text);
          margin-top: 12px;
          padding: 10px 12px;
          background: var(--danger-bg);
          border: 1px solid var(--danger-border);
          border-radius: var(--radius-sm);
        }

        .auth-success {
          font-size: 12px;
          color: var(--success-text, #22c55e);
          margin-top: 12px;
          padding: 10px 12px;
          background: var(--success-bg, rgba(34,197,94,0.08));
          border: 1px solid var(--success-border, rgba(34,197,94,0.25));
          border-radius: var(--radius-sm);
        }

        .otp-email-badge {
          display: inline-block;
          font-size: 12px;
          font-weight: 500;
          color: var(--accent);
          background: var(--bg-hover);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-sm);
          padding: 4px 10px;
          margin-bottom: 20px;
          word-break: break-all;
        }

        .otp-input {
          letter-spacing: 0.35em;
          font-size: 22px;
          text-align: center;
          font-family: var(--font-mono, monospace);
        }
      `}</style>

      <div className="auth-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="auth-modal" style={{ position: "relative" }}>

          <button className="auth-close" onClick={onClose}>×</button>

          <div className={`auth-card ${flipping ? "flipping" : ""}`}>

            {/* Tab switcher — hidden on verify screen */}
            {mode !== "verify" && (
              <div className="auth-top-bar">
                <button
                  className={`auth-tab ${mode === "login" ? "active" : ""}`}
                  onClick={() => flipTo("login")}
                >
                  Sign in
                </button>
                <button
                  className={`auth-tab ${mode === "register" ? "active" : ""}`}
                  onClick={() => flipTo("register")}
                >
                  Register
                </button>
              </div>
            )}

            <div className="auth-body" onKeyDown={handleKey}>

              {/* Logo */}
              <div className="auth-logo">
                <span className="tc-dot" />
                <span className="auth-logo-text">TableCure</span>
              </div>

              {/* ── Login form ── */}
              {mode === "login" && (
                <>
                  <h2 className="auth-title">Welcome back</h2>
                  <p className="auth-subtitle">Sign in to your workspace account.</p>

                  <div className="tc-field">
                    <label className="tc-label">Email</label>
                    <input
                      className="tc-input"
                      type="email"
                      placeholder="you@example.com"
                      value={loginForm.email}
                      onChange={e => { setLoginForm({ ...loginForm, email: e.target.value }); setError(""); }}
                    />
                  </div>

                  <div className="tc-field">
                    <label className="tc-label">Password</label>
                    <input
                      className="tc-input"
                      type="password"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={e => { setLoginForm({ ...loginForm, password: e.target.value }); setError(""); }}
                    />
                  </div>

                  {error && <div className="auth-error">{error}</div>}

                  <div className="tc-btn-row" style={{ marginTop: "20px" }}>
                    <button
                      className="tc-btn tc-btn-primary flex-1 justify-center"
                      style={{ padding: "12px", fontSize: "12px" }}
                      onClick={handleLogin}
                      disabled={loading}
                    >
                      {loading ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="tc-spinner" style={{ width: 14, height: 14, borderWidth: 1.5 }} />
                          Signing in...
                        </span>
                      ) : "Sign in"}
                    </button>
                  </div>

                  <div className="auth-footer">
                    Don't have an account?{" "}
                    <button className="auth-footer-link" onClick={() => flipTo("register")}>
                      Create one
                    </button>
                  </div>

                  <div className="auth-footer" style={{ marginTop: 8 }}>
                    <button
                      className="auth-footer-link"
                      onClick={() => {
                        onClose();
                        navigate(`/forgot-password${loginForm.email ? `?email=${encodeURIComponent(loginForm.email)}` : ""}`);
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                </>
              )}

              {/* ── Register form ── */}
              {mode === "register" && (
                <>
                  <h2 className="auth-title">Create account</h2>
                  <p className="auth-subtitle">Join TableCure and light up your workspace.</p>

                  <div className="tc-field">
                    <label className="tc-label">Full Name</label>
                    <input
                      className="tc-input"
                      placeholder="Sachin Jangid"
                      value={registerForm.name}
                      onChange={e => { setRegisterForm({ ...registerForm, name: e.target.value }); setError(""); }}
                    />
                  </div>

                  <div className="tc-field">
                    <label className="tc-label">Email</label>
                    <input
                      className="tc-input"
                      type="email"
                      placeholder="you@example.com"
                      value={registerForm.email}
                      onChange={e => { setRegisterForm({ ...registerForm, email: e.target.value }); setError(""); }}
                    />
                  </div>

                  <div className="tc-field">
                    <label className="tc-label">Password</label>
                    <input
                      className="tc-input"
                      type="password"
                      placeholder="Min. 6 characters"
                      value={registerForm.password}
                      onChange={e => { setRegisterForm({ ...registerForm, password: e.target.value }); setError(""); }}
                    />
                  </div>

                  {error === "__account_exists__" ? (
                    <div className="auth-error">
                      An account with this email already exists.{" "}
                      <button
                        className="auth-footer-link"
                        style={{ fontSize: "inherit" }}
                        onClick={() => {
                          setLoginForm(f => ({ ...f, email: registerForm.email }));
                          flipTo("login");
                        }}
                      >
                        Want to sign in?
                      </button>
                    </div>
                  ) : error ? (
                    <div className="auth-error">{error}</div>
                  ) : null}

                  <div className="tc-btn-row" style={{ marginTop: "20px" }}>
                    <button
                      className="tc-btn tc-btn-primary flex-1 justify-center"
                      style={{ padding: "12px", fontSize: "12px" }}
                      onClick={handleRegister}
                      disabled={loading}
                    >
                      {loading ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="tc-spinner" style={{ width: 14, height: 14, borderWidth: 1.5 }} />
                          Creating account...
                        </span>
                      ) : "Create account"}
                    </button>
                  </div>

                  <div className="auth-footer">
                    Already have an account?{" "}
                    <button className="auth-footer-link" onClick={() => flipTo("login")}>
                      Sign in
                    </button>
                  </div>
                </>
              )}

              {/* ── Verify OTP ── */}
              {mode === "verify" && (
                <>
                  <h2 className="auth-title">Verify email</h2>
                  <p className="auth-subtitle">
                    A 6-digit code was sent to:
                  </p>
                  <span className="otp-email-badge">{pendingEmail}</span>

                  <div className="tc-field">
                    <label className="tc-label">One-time code</label>
                    <input
                      className="tc-input otp-input"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="––––––"
                      value={otp}
                      onChange={e => { setOtp(e.target.value.replace(/\D/g, "")); setError(""); }}
                      autoFocus
                    />
                  </div>

                  {error   && <div className="auth-error">{error}</div>}
                  {success && <div className="auth-success">{success}</div>}

                  <div className="tc-btn-row" style={{ marginTop: "20px" }}>
                    <button
                      className="tc-btn tc-btn-primary flex-1 justify-center"
                      style={{ padding: "12px", fontSize: "12px" }}
                      onClick={handleVerify}
                      disabled={loading}
                    >
                      {loading ? (
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="tc-spinner" style={{ width: 14, height: 14, borderWidth: 1.5 }} />
                          Verifying...
                        </span>
                      ) : "Verify & continue"}
                    </button>
                  </div>

                  <div className="auth-footer">
                    Didn't receive it?{" "}
                    <button
                      className="auth-footer-link"
                      onClick={handleResend}
                      disabled={resendCooldown > 0}
                    >
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                    </button>
                  </div>

                  <div className="auth-footer" style={{ marginTop: 8 }}>
                    <button className="auth-footer-link" onClick={() => flipTo("login")}>
                      Back to sign in
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthModal;
